"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ButtonToggle = exports.ButtonToggleElement = void 0;
// ButtonToggle.ts
const web_components_1 = require("../web-components");
const ButtonToggleStyles_1 = require("./ButtonToggleStyles");
class ButtonToggleElement extends HTMLElement {
    buttons = [];
    hasRendered = false;
    previousValue = null;
    previousOptions = null;
    static get observedAttributes() {
        return ["value", "options", "disabled", "name"];
    }
    // ---- public API --------------------------------------------------------
    get value() {
        return this.getAttribute("value") ?? "";
    }
    set value(v) {
        if (v === null || v === undefined || v === "") {
            this.removeAttribute("value");
        }
        else {
            this.setAttribute("value", v);
        }
    }
    get disabled() {
        return this.hasAttribute("disabled");
    }
    set disabled(v) {
        if (v)
            this.setAttribute("disabled", "");
        else
            this.removeAttribute("disabled");
    }
    setOptions(options) {
        this.setAttribute("options", JSON.stringify(options));
    }
    // ---- lifecycle --------------------------------------------------------
    connectedCallback() {
        // Inject styles once into document head
        // this.injectStyles();
        this.render();
    }
    attributeChangedCallback(name, oldValue, newValue) {
        // Skip initial render (handled by connectedCallback)
        if (!this.hasRendered)
            return;
        // Only re-render if options changed (full rebuild needed)
        if (name === "options" && oldValue !== newValue) {
            this.previousOptions = newValue;
            this.render();
            return;
        }
        // For value/disabled/name changes, update incrementally
        if (name === "value" && oldValue !== newValue) {
            this.updateSelectedButton(newValue ?? "");
            return;
        }
        if (name === "disabled") {
            this.updateDisabledState();
            return;
        }
        if (name === "name") {
            this.updateButtonNames(newValue ?? "");
            return;
        }
    }
    disconnectedCallback() {
        this.removeEventListeners();
    }
    // ---- helpers ----------------------------------------------------------
    // private injectStyles() {
    //     const styleId = "button-toggle-styles";
    //     // const id = this.getAttribute("id") || `button-toggle-${Date.now()}`;
    //     if (document.getElementById(styleId)) return;
    //     const styleEl = document.createElement("style");
    //     styleEl.id = styleId;
    //     styleEl.textContent = `
    //         .ButtonToggle {
    //             ${styles.buttonToggle}
    //         }
    //         .ButtonToggle__button {
    //             ${styles.button}
    //         }
    //         .ButtonToggle__button:hover {
    //             ${styles.buttonHover}
    //         }
    //         .ButtonToggle__button--selected {
    //             ${styles.selected}
    //         }
    //         .ButtonToggle[aria-disabled="true"] {
    //             ${styles.disabled}
    //         }
    //         .ButtonToggle__button dc-button::part(button) {
    //             ${styles.dcButton}
    //         }
    //         .ButtonToggle__button--selected dc-button::part(button) {
    //             ${styles.dcSelected}
    //         }
    //     `;
    //     document.head.appendChild(styleEl);
    // }
    parseOptions() {
        const raw = this.getAttribute("options") || "[]";
        try {
            const arr = JSON.parse(raw);
            if (!Array.isArray(arr))
                return [];
            return arr
                .map((o) => ({
                value: String(o?.value ?? ""),
                label: String(o?.label ?? ""),
                disabled: !!o?.disabled,
            }))
                .filter((o) => o.value !== "" && o.label !== "");
        }
        catch {
            console.warn("[ButtonToggle] Invalid JSON in `options`");
            return [];
        }
    }
    handleButtonClick = (event) => {
        const button = event.currentTarget;
        const newValue = button.dataset.value ?? "";
        if (!newValue || this.disabled || newValue === this.value)
            return;
        // Update selected value (triggers attributeChangedCallback → incremental update)
        this.value = newValue;
        // Bubble a change event outward
        this.dispatchEvent(new CustomEvent("change", {
            detail: { value: newValue },
            bubbles: true,
            composed: true,
        }));
    };
    removeEventListeners() {
        this.buttons.forEach((btn) => {
            btn.removeEventListener("click", this.handleButtonClick);
        });
    }
    // Incremental update: only toggle selected state on buttons
    updateSelectedButton(newValue) {
        this.buttons.forEach((btn) => {
            const isSelected = btn.dataset.value === newValue;
            btn.classList.toggle("ButtonToggle__button--selected", isSelected);
        });
        this.previousValue = newValue;
    }
    // Incremental update: toggle disabled on container & buttons
    updateDisabledState() {
        const container = this.querySelector(".ButtonToggle");
        if (!container)
            return;
        const isDisabled = this.disabled;
        container.setAttribute("aria-disabled", String(isDisabled));
        this.buttons.forEach((btn) => {
            const dcButton = btn.querySelector("dc-button");
            if (dcButton) {
                if (isDisabled)
                    dcButton.setAttribute("disabled", "");
                else
                    dcButton.removeAttribute("disabled");
            }
        });
    }
    // Incremental update: sync name attribute
    updateButtonNames(newName) {
        this.buttons.forEach((btn) => {
            const dcButton = btn.querySelector("dc-button");
            if (dcButton) {
                if (newName)
                    dcButton.setAttribute("name", newName);
                else
                    dcButton.removeAttribute("name");
            }
        });
    }
    // Full render: only called on connectedCallback or options change
    render() {
        console.log("render(): [ButtonToggle] Render called ---------------");
        const id = this.getAttribute("id") || `button-toggle-${Date.now()}`;
        const name = this.getAttribute("name") ?? "";
        const options = this.parseOptions();
        // Determine selected value: attribute → first option fallback
        let selectedValue = this.value;
        if (!selectedValue && options.length > 0) {
            selectedValue = options[0].value;
            super.setAttribute("value", selectedValue);
        }
        const buttonsMarkup = options
            .map((option) => {
            const isSelected = option.value === selectedValue;
            const disabled = this.disabled || option.disabled;
            return `
                    <button
                        class="ButtonToggle__button ${isSelected ? "ButtonToggle__button--selected" : ""}"
                        type="button"
                        data-value="${(0, web_components_1.escapeHTMLChars)(option.value)}"
                        part="buttonToggleButton"
                    >
                        <dc-button
                            size="sm"
                            label="${(0, web_components_1.escapeHTMLChars)(option.label)}" 
                            variant="tertiary-primary"
                            ${disabled ? "disabled" : ""}
                            ${name ? `name="${(0, web_components_1.escapeHTMLChars)(name)}"` : ""}
                        ></dc-button>
                    </button>
                `;
        })
            .join("");
        this.innerHTML = `
            <style>
                .ButtonToggle {${ButtonToggleStyles_1.styles.buttonToggle}}
                .ButtonToggle__button {${ButtonToggleStyles_1.styles.button} cursor: pointer;}
                .ButtonToggle__button:hover {${ButtonToggleStyles_1.styles.buttonHover}}
                .ButtonToggle__button dc-button::part(button) {${ButtonToggleStyles_1.styles.dcButton} cursor: pointer;}
                .ButtonToggle__button--selected {${ButtonToggleStyles_1.styles.selected}}
                .ButtonToggle__button--selected dc-button::part(button){${ButtonToggleStyles_1.styles.dcSelected}}
            </style>

            <div id="${id}" class="ButtonToggle" ${this.disabled ? 'aria-disabled="true"' : ""} part="buttonToggle">
                ${buttonsMarkup}
            </div>
        `;
        // Cache button references and attach listeners
        this.removeEventListeners();
        this.buttons = Array.from(this.querySelectorAll(".ButtonToggle__button"));
        this.buttons.forEach((btn) => {
            btn.addEventListener("click", this.handleButtonClick);
        });
        this.hasRendered = true;
        this.previousValue = selectedValue;
        this.previousOptions = this.getAttribute("options");
    }
}
exports.ButtonToggleElement = ButtonToggleElement;
const ButtonToggle = (props) => {
    const { id, name, value, disabled, options } = props.attributes;
    const finalId = id || `button-toggle-${Date.now()}`;
    const initialValue = value ?? (options && options.length ? options[0].value : "");
    const optionsAttr = options && options.length
        ? `options='${JSON.stringify(options).replace(/'/g, "&apos;")}'`
        : "";
    return `<button-toggle
        ${(0, web_components_1.toAttribute)("id", finalId)}
        ${(0, web_components_1.toAttribute)("name", name)}
        ${(0, web_components_1.toAttribute)("value", initialValue)}
        ${optionsAttr}
        ${disabled ? "disabled" : ""}>
    </button-toggle>`;
};
exports.ButtonToggle = ButtonToggle;
(0, web_components_1.registerWebComponent)("button-toggle", ButtonToggleElement);
//# sourceMappingURL=ButtonToggle.js.map